import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    AlignmentType,
    ImageRun,
    convertInchesToTwip,
    Header,
    Footer,
    ExternalHyperlink,
} from "docx";
import { saveAs } from "file-saver";

interface ArticleData {
    _id: string;
    title: string;
    content:
    | {
        ops?: Array<{
            insert?: string | { image?: string };
            attributes?: {
                bold?: boolean;
                header?: number;
                list?: string;
            };
        }>;
    }
    | string;
    introImage?: string;
    standard?: string;
    courseName?: string;
    category?: string;
    createdAt?: string;
}

/**
 * Convert base64 image to buffer
 */
const base64ToBuffer = (base64: string): Uint8Array => {
    // Remove data URL prefix if present
    const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
};

/**
 * Load logo image from public folder
 */
const loadLogoImage = async (): Promise<Uint8Array | null> => {
    try {
        const logoResponse = await fetch("/logo192.png");
        if (logoResponse.ok) {
            const logoBlob = await logoResponse.blob();
            const arrayBuffer = await logoBlob.arrayBuffer();
            return new Uint8Array(arrayBuffer);
        }
    } catch (error) {
        console.warn("Could not load logo for Word document:", error);
    }
    return null;
};


/**
 * Convert Quill Delta content to Word document paragraphs
 */
const quillDeltaToWordParagraphs = (
    content: ArticleData["content"],
    introImageToSkip?: string
): Paragraph[] => {
    const paragraphs: Paragraph[] = [];

    // Handle string content (HTML)
    if (typeof content === "string") {
        // Simple HTML to text conversion
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const text = tempDiv.textContent || "";

        text.split("\n").forEach((line) => {
            if (line.trim()) {
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: line,
                                font: "Hind Vadodara",
                            }),
                        ],
                        spacing: {
                            after: 200,
                        },
                    })
                );
            }
        });
        return paragraphs;
    }

    // Handle empty or invalid content
    if (
        !content ||
        typeof content !== "object" ||
        !content.ops ||
        !Array.isArray(content.ops)
    ) {
        return paragraphs;
    }

    let currentListItems: Paragraph[] = [];
    let currentListType: "bullet" | "ordered" | null = null;

    content.ops.forEach((op, index) => {
        if (typeof op.insert === "string") {
            let text = op.insert;

            // Skip empty text (except newlines)
            if (!text.trim() && text !== "\n") {
                return;
            }

            // Handle newlines
            if (text === "\n") {
                // Flush list if we have one
                if (currentListItems.length > 0) {
                    paragraphs.push(...currentListItems);
                    currentListItems = [];
                    currentListType = null;
                }
                return;
            }

            // Process text with attributes
            if (op.attributes) {
                if (op.attributes.header) {
                    // Flush list if we have one
                    if (currentListItems.length > 0) {
                        paragraphs.push(...currentListItems);
                        currentListItems = [];
                        currentListType = null;
                    }

                    const level = op.attributes.header;
                    const headingLevel =
                        level === 1
                            ? HeadingLevel.HEADING_1
                            : level === 2
                                ? HeadingLevel.HEADING_2
                                : HeadingLevel.HEADING_3;

                    paragraphs.push(
                        new Paragraph({
                            text: text,
                            heading: headingLevel,
                            spacing: {
                                before: 240,
                                after: 120,
                            },
                        })
                    );
                } else if (
                    op.attributes.list === "bullet" ||
                    op.attributes.list === "ordered"
                ) {
                    const listType = op.attributes.list;

                    // If list type changed, flush previous list
                    if (currentListType && currentListType !== listType) {
                        paragraphs.push(...currentListItems);
                        currentListItems = [];
                    }

                    currentListType = listType;

                    currentListItems.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: text,
                                    bold: op.attributes.bold || false,
                                    font: "Hind Vadodara",
                                }),
                            ],
                            bullet: {
                                level: 0,
                            },
                            spacing: {
                                after: 100,
                            },
                        })
                    );
                } else {
                    // Flush list if we have one
                    if (currentListItems.length > 0) {
                        paragraphs.push(...currentListItems);
                        currentListItems = [];
                        currentListType = null;
                    }

                    if (text.trim()) {
                        paragraphs.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: text,
                                        bold: op.attributes.bold || false,
                                        font: "Hind Vadodara",
                                    }),
                                ],
                                spacing: {
                                    after: 200,
                                },
                            })
                        );
                    }
                }
            } else {
                // Flush list if we have one
                if (currentListItems.length > 0) {
                    paragraphs.push(...currentListItems);
                    currentListItems = [];
                    currentListType = null;
                }

                if (text.trim()) {
                    paragraphs.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: text,
                                    font: "Hind Vadodara",
                                }),
                            ],
                            spacing: {
                                after: 200,
                            },
                        })
                    );
                }
            }
        } else if (op.insert && typeof op.insert === "object" && op.insert.image) {
            // Flush list if we have one
            if (currentListItems.length > 0) {
                paragraphs.push(...currentListItems);
                currentListItems = [];
                currentListType = null;
            }

            const imageData = op.insert.image;
            if (imageData && typeof imageData === "string") {
                // Skip if this is the intro image (to avoid duplication)
                if (introImageToSkip) {
                    const isSameImage =
                        imageData === introImageToSkip ||
                        (imageData.includes(",") &&
                            introImageToSkip.includes(",") &&
                            imageData.split(",")[1] === introImageToSkip.split(",")[1]);
                    if (isSameImage) {
                        return;
                    }
                }

                try {
                    const imageBuffer = base64ToBuffer(imageData);
                    paragraphs.push(
                        new Paragraph({
                            children: [
                                new ImageRun({
                                    type: "png",
                                    data: imageBuffer,
                                    transformation: {
                                        width: 500,
                                        height: 300,
                                    },
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: {
                                before: 200,
                                after: 200,
                            },
                        })
                    );
                } catch (error) {
                    console.error("Error adding image to Word document:", error);
                }
            }
        }
    });

    // Flush any remaining list items
    if (currentListItems.length > 0) {
        paragraphs.push(...currentListItems);
    }

    return paragraphs;
};

/**
 * Generate Word document from article data
 */
export const generateArticleWord = async (
    article: ArticleData
): Promise<void> => {
    const sections: Paragraph[] = [];

    // Add metadata section
    const metadata: Paragraph[] = [];

    // Add date at the top
    if (article.createdAt) {
        const date = new Date(article.createdAt);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const formattedDate = `${dd}-${mm}-${yyyy}`;
        metadata.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Date: ",
                        bold: true,
                        font: "Hind Vadodara",
                    }),
                    new TextRun({
                        text: formattedDate,
                        font: "Hind Vadodara",
                    }),
                ],
                spacing: {
                    after: 100,
                },
            })
        );
    }

    if (article.standard) {
        metadata.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Education: ",
                        bold: true,
                        font: "Hind Vadodara",
                    }),
                    new TextRun({
                        text: article.standard,
                        font: "Hind Vadodara",
                    }),
                ],
                spacing: {
                    after: 100,
                },
            })
        );
    }

    if (article.courseName) {
        metadata.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Stream: ",
                        bold: true,
                        font: "Hind Vadodara",
                    }),
                    new TextRun({
                        text: article.courseName,
                        font: "Hind Vadodara",
                    }),
                ],
                spacing: {
                    after: 100,
                },
            })
        );
    }

    if (article.category) {
        metadata.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Category: ",
                        bold: true,
                        font: "Hind Vadodara",
                    }),
                    new TextRun({
                        text: article.category,
                        font: "Hind Vadodara",
                    }),
                ],
                spacing: {
                    after: 200,
                },
            })
        );
    }

    sections.push(...metadata);

    // Add horizontal line (using border)
    sections.push(
        new Paragraph({
            border: {
                bottom: {
                    color: "333333",
                    space: 1,
                    style: "single",
                    size: 6,
                },
            },
            spacing: {
                after: 200,
            },
        })
    );

    // Add title
    sections.push(
        new Paragraph({
            text: article.title,
            heading: HeadingLevel.HEADING_1,
            spacing: {
                after: 200,
            },
        })
    );

    // Add intro image if available
    if (article.introImage) {
        try {
            const imageBuffer = base64ToBuffer(article.introImage);
            sections.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            type: "png",
                            data: imageBuffer,
                            transformation: {
                                width: 500,
                                height: 300,
                            },
                        }),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: {
                        before: 200,
                        after: 200,
                    },
                })
            );
        } catch (error) {
            console.error("Error adding intro image to Word document:", error);
        }
    }

    // Add content
    const contentParagraphs = quillDeltaToWordParagraphs(
        article.content,
        article.introImage
    );
    sections.push(...contentParagraphs);

    // Load logo for header
    const logoBuffer = await loadLogoImage();

    // Create header with logo
    const headerChildren: Paragraph[] = [];
    if (logoBuffer) {
        headerChildren.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        type: "png",
                        data: logoBuffer,
                        transformation: {
                            width: 80,
                            height: 30,
                        },
                    }),
                ],
                alignment: AlignmentType.RIGHT,
            })
        );
    }

    // Create footer with contact information
    const footer = new Footer({
        children: [
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Website: ",
                        size: 18,
                        color: "666666",
                    }),
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: "www.gk360app.com",
                                size: 18,
                                color: "0000FF",
                                underline: {},
                            }),
                        ],
                        link: "https://www.gk360app.com",
                    }),
                    new TextRun({
                        text: "  |  Email: ",
                        size: 18,
                        color: "666666",
                    }),
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: "namaste@gk360app.com",
                                size: 18,
                                color: "0000FF",
                                underline: {},
                            }),
                        ],
                        link: "mailto:namaste@gk360app.com",
                    }),
                    new TextRun({
                        text: "  |  WhatsApp: ",
                        size: 18,
                        color: "666666",
                    }),
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: "+919409200300",
                                size: 18,
                                color: "0000FF",
                                underline: {},
                            }),
                        ],
                        link: "https://wa.me/919409200300",
                    }),
                ],
                alignment: AlignmentType.CENTER,
            }),
        ],
    });

    // Create document with header and footer
    const doc = new Document({
        sections: [
            {
                properties: {},
                headers: {
                    default: new Header({
                        children: headerChildren,
                    }),
                },
                footers: {
                    default: footer,
                },
                children: sections,
            },
        ],
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);

    // Clean title to preserve Gujarati characters (same as PDF generator)
    let cleanTitle = article.title || "article";
    cleanTitle = cleanTitle
        .replace(/[<>:"/\\|?*]/g, "")
        .replace(/\s+/g, "_")
        .replace(/[^\w\u0A80-\u0AFF_-]/g, "")
        .substring(0, 100)
        .trim();

    if (!cleanTitle || cleanTitle.length === 0) {
        cleanTitle = `article_${Date.now()}`;
    }

    const fileName = `${cleanTitle}.docx`;
    saveAs(blob, fileName);
};
